import { Box, CircularProgress, Typography } from '@mui/material';
import { Download } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import IncompleteRetrievalDialog from '~/hooks/openInViewer/IncompleteRetrievalDialog';
import { useLoadDataset } from '~/hooks/openInViewer/useLoadDataset';
import {
    unloadedDatasets,
    type UnloadedDataset,
} from '~/hooks/openInViewer/unloadedDatasets';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { connectorPatientManifestState } from '~/state/atoms/connectorManifestAtoms';

/** Message id naming each dataset kind, for a row with no description. */
const KIND_LABEL: Record<UnloadedDataset['kind'], string> = {
    Image3D: 'image',
    StructureSet: 'structureSet',
    Dose: 'dose',
    Registration: 'registration',
};

/**
 * The rest of the patient: datasets the Connector holds that this session did
 * not retrieve, each loadable on the spot.
 *
 * An open brings what the workflow needs. Everything else stays on the
 * Connector, described by the manifest — which has always covered the whole
 * patient rather than the selection — so this list is what makes that
 * narrowing something a user can undo rather than a wall.
 */
export default function UnloadedDatasetList() {
    const intl = useIntl();
    const manifest = useRecoilValue(connectorPatientManifestState);
    const patient = useSelectedPatient();
    const { loadDataset, loadingDatasetId, incompleteRetrieval } =
        useLoadDataset(manifest?.patientId);

    const datasets = unloadedDatasets(manifest ?? undefined, patient);
    // Rendered whether or not a row is left: the question belongs to a load
    // already under way, and must outlive the list emptying under it.
    const dialog = (
        <IncompleteRetrievalDialog
            state={incompleteRetrieval.state}
            onResolve={incompleteRetrieval.settle}
            action="load"
        />
    );
    if (datasets.length === 0) {
        return dialog;
    }

    return (
        <Box>
            {dialog}
            <Box px={2} pt={1.5} pb={1}>
                <Typography fontSize={12} color="primary" fontWeight={500}>
                    <FormattedMessage id="setup.notLoaded" />
                </Typography>
            </Box>
            {datasets.map((dataset) => (
                <Box
                    key={dataset.datasetId}
                    borderTop="1px solid"
                    borderColor="divider"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                    }}
                >
                    <Typography
                        noWrap
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 12,
                            color: 'text.secondary',
                        }}
                    >
                        {dataset.seriesDescription ||
                            intl.formatMessage({
                                id: KIND_LABEL[dataset.kind],
                            })}
                    </Typography>
                    <Box
                        component="button"
                        type="button"
                        disabled={loadingDatasetId !== undefined}
                        aria-label={intl.formatMessage(
                            { id: 'setup.loadDataset' },
                            {
                                name:
                                    dataset.seriesDescription ||
                                    intl.formatMessage({
                                        id: KIND_LABEL[dataset.kind],
                                    }),
                            }
                        )}
                        onClick={() => loadDataset(dataset.datasetId)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            border: 0,
                            borderRadius: 1,
                            fontFamily: 'inherit',
                            fontSize: 11,
                            color: 'text.primary',
                            bgcolor: 'background.raised',
                            cursor: 'pointer',
                            '&:disabled': {
                                color: 'action.disabled',
                                cursor: 'default',
                            },
                        }}
                    >
                        {loadingDatasetId === dataset.datasetId ? (
                            <CircularProgress size={11} />
                        ) : (
                            <Download size={11} />
                        )}
                        <FormattedMessage id="setup.load" />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}
